import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Typography,
  Chip,
  Box,
  useTheme,
  alpha,
  Tooltip,
  CircularProgress
} from '@mui/material';

// تعريف واجهة بيانات المخزون
interface InventoryItem {
  id: string;
  barcode: string;
  productName: string;
  variant: string;
  category: string;
  allCategories: string[];
  categoryCount: number;
  warehouses: {
    [key: string]: number; // اسم المستودع: الكمية
  };
}

// تعريف واجهة خصائص الجدول
interface InventoryReportTableProps {
  data: InventoryItem[];
  warehouses: string[];
  totalItems: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (event: unknown, newPage: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  warehouseTotals?: { [key: string]: number }; // إجماليات كل مستودع من السيرفر
  grandTotal?: number; // الإجمالي الكلي من السيرفر
  loadingTotals?: boolean; // حالة تحميل الإجماليات
}

// مكون جدول تقرير المخزون
const InventoryReportTable: React.FC<InventoryReportTableProps> = ({
  data,
  warehouses,
  totalItems,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  warehouseTotals: serverWarehouseTotals,
  grandTotal: serverGrandTotal,
  loadingTotals = false
}) => {
  const theme = useTheme();

  // تحديد لون الكمية بناءً على القيمة
  const getQuantityColor = (quantity: number) => {
    if (quantity <= 0) {
      return '#d32f2f'; // أحمر للكمية الصفر
    } else if (quantity < 5) {
      return '#f57c00'; // برتقالي للكمية المنخفضة
    } else if (quantity < 10) {
      return '#fdd835'; // أصفر للكمية المتوسطة
    } else {
      return '#4caf50'; // أخضر للكمية العالية
    }
  };

  // حساب الكمية الإجمالية للمنتج (فقط من المستودعات المعروضة)
  const calculateTotalQuantity = (item: InventoryItem) => {
    return warehouses.reduce((sum, warehouse) => {
      return sum + (item.warehouses[warehouse] || 0);
    }, 0);
  };
  
  // حساب إجمالي كميات كل مستودع للصفحة الحالية (للعرض المحلي فقط)
  const calculateCurrentPageWarehouseTotals = () => {
    const totals: { [key: string]: number } = {};
    
    // تهيئة إجماليات المستودعات بقيمة صفر
    warehouses.forEach(warehouse => {
      totals[warehouse] = 0;
    });
    
    // حساب الإجماليات للصفحة الحالية
    data.forEach(item => {
      warehouses.forEach(warehouse => {
        totals[warehouse] += item.warehouses[warehouse] || 0;
      });
    });
    
    return totals;
  };
  
  // حساب الإجمالي الكلي للصفحة الحالية
  const calculateCurrentPageGrandTotal = () => {
    return data.reduce((total, item) =>
      total + calculateTotalQuantity(item), 0);
  };
  
  // استخدام الإجماليات من السيرفر إذا كانت متوفرة، وإلا استخدم حساب الصفحة الحالية
  const warehouseTotals = serverWarehouseTotals || calculateCurrentPageWarehouseTotals();
  const grandTotal = serverGrandTotal !== undefined ? serverGrandTotal : calculateCurrentPageGrandTotal();

  return (
    <Box>
      <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
        <Table stickyHeader aria-label="جدول تقرير المخزون">
          <TableHead>
            <TableRow>
              <TableCell 
                sx={{ 
                  fontWeight: 'bold', 
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main
                }}
              >
                الباركود
              </TableCell>
              <TableCell 
                sx={{ 
                  fontWeight: 'bold', 
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main
                }}
              >
                المنتج
              </TableCell>
              <TableCell 
                sx={{ 
                  fontWeight: 'bold', 
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main
                }}
              >
                المتغير
              </TableCell>
              <TableCell 
                sx={{ 
                  fontWeight: 'bold', 
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main
                }}
              >
                التصنيف
              </TableCell>
              {warehouses.map(warehouse => (
                <TableCell 
                  key={warehouse}
                  align="center"
                  sx={{ 
                    fontWeight: 'bold', 
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main
                  }}
                >
                  {warehouse}
                </TableCell>
              ))}
              <TableCell 
                align="center"
                sx={{ 
                  fontWeight: 'bold', 
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main
                }}
              >
                الإجمالي
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length > 0 ? (
              data.map((item) => (
                <TableRow
                  key={item.id}
                  sx={{
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.04),
                    },
                    transition: 'background-color 0.2s'
                  }}
                >
                  <TableCell sx={{ fontFamily: 'monospace' }}>{item.barcode}</TableCell>
                  <TableCell>{item.productName}</TableCell>
                  <TableCell>{item.variant}</TableCell>
                  <TableCell>
                    {item.categoryCount > 1 ? (
                      <Tooltip
                        title={
                          <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                              جميع التصنيفات:
                            </Typography>
                            {item.allCategories.map((cat, index) => (
                              <Typography key={index} variant="body2">
                                • {cat}
                              </Typography>
                            ))}
                          </Box>
                        }
                        arrow
                        placement="top"
                      >
                        <Chip
                          label={item.category}
                          size="small"
                          sx={{
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                            fontWeight: 500,
                            cursor: 'help'
                          }}
                        />
                      </Tooltip>
                    ) : (
                      <Chip
                        label={item.category}
                        size="small"
                        sx={{
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main,
                          fontWeight: 500
                        }}
                      />
                    )}
                  </TableCell>
                  {warehouses.map(warehouse => {
                    const quantity = item.warehouses[warehouse] || 0;
                    const quantityColor = getQuantityColor(quantity);
                    
                    return (
                      <TableCell key={warehouse} align="center">
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 'bold',
                            color: quantityColor
                          }}
                        >
                          {quantity}
                        </Typography>
                      </TableCell>
                    );
                  })}
                  <TableCell align="center">
                    <Chip
                      label={calculateTotalQuantity(item)}
                      sx={{
                        bgcolor: '#30c3b0',
                        color: 'white',
                        fontWeight: 'bold'
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5 + warehouses.length} align="center" sx={{ py: 4 }}>
                  <Typography variant="subtitle1" color="text.secondary">
                    لا توجد بيانات للعرض
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* صف الإجمالي */}
      <Paper
        elevation={2}
        sx={{
          mt: 2,
          mb: 2,
          p: 1.5,
          borderRadius: 2,
          backgroundColor: alpha(theme.palette.primary.main, 0.05)
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ color: theme.palette.primary.main }}>
            إجماليات المخزون:
          </Typography>
          
          {loadingTotals ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary">
                جاري حساب الإجماليات...
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {warehouses.map(warehouse => (
                <Chip
                  key={warehouse}
                  label={
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Typography variant="caption">{warehouse}</Typography>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {warehouseTotals[warehouse] !== undefined ? warehouseTotals[warehouse] : '-'}
                      </Typography>
                    </Box>
                  }
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    borderRadius: 1,
                    p: 0.5,
                    height: 'auto',
                    '& .MuiChip-label': { p: 1 }
                  }}
                />
              ))}
              
              <Chip
                label={
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Typography variant="caption">الإجمالي الكلي</Typography>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {grandTotal !== undefined ? grandTotal : '-'}
                    </Typography>
                  </Box>
                }
                sx={{
                  bgcolor: '#30c3b0',
                  color: 'white',
                  borderRadius: 1,
                  p: 0.5,
                  height: 'auto',
                  '& .MuiChip-label': { p: 1 }
                }}
              />
            </Box>
          )}
        </Box>
      </Paper>
      
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={totalItems}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        labelRowsPerPage="عدد الصفوف:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} من ${count}`}
      />
    </Box>
  );
};

export default InventoryReportTable;